/*  ────────────────────────────────────────────────────────────────
    /cadence  ·  Cadence × LesConcierges® employee-benefit landing.
    1:1 replica of https://mindescapes.club/cadence — this is a
    view-only marketing surface. Every CTA hands off to the member
    platform on mindescapes.club/cadence/*. No forms, no auth, no
    Zoho webhook here — those live on the destination platform.

    Design tokens (locked to the live page):
      · Heading font : Fraunces (Google)
      · Body font    : Archivo  (Google)
      · Accent red   : #E4002B
      · Ink          : #1A1A1A
      · Page bg      : #F6F3EA (warm off-white + grain)
      · Alt section  : #F3F0E6
      · Dark band    : #141618
      · Gold (dark)  : #c9a961   ·   Gold (light) : #a98a4b
      · Card fill    : #FBFAF4   ·   Card radius  : 18px

    Brief : /app/LC_AGENT_BRIEF_CADENCE.md
    Built : 07 Jul 2026
    ──────────────────────────────────────────────────────────────── */

import React from "react";

/* ─── Handoff links (all point at mindescapes.club platform) ────── */
const MC = "https://mindescapes.club/cadence";
const LINKS = {
    signIn:    `${MC}/signin`,
    signUp:    `${MC}/signup`,
    concierge: `${MC}/club/concierge`,
    terms:     `${MC}/terms`,
    whatsapp:  "https://wa.me/918792622424?text=Hi%2C%20I%27m%20a%20Cadence%20employee%20and%20would%20like%20some%20help.",
};

/* ─── Cloudinary sources ────────────────────────────────────────────
   These are FALLBACKS. On mount we fetch the live tenant JSON from
   MindEscapes so the page always mirrors whatever Dipali has staged
   in the CMS — no redeploy needed when images/copy change.
   See §5 in /app/LC_AGENT_BRIEF_CADENCE.md. */
const IMG = {
    logoChip:  "https://res.cloudinary.com/datmqdk2c/image/upload/f_auto,q_auto,w_120/v1783419882/mindescapes/tenant-logos/cadence.png",
    heroTall:  "https://res.cloudinary.com/datmqdk2c/image/upload/f_auto,q_auto,w_900,h_560,c_fill,g_auto/v1783426402/mindescapes/benefits/wlhj3kyyiwgotzvdfxzw.png",
    heroTopR:  "https://res.cloudinary.com/datmqdk2c/image/upload/f_auto,q_auto,w_900,h_560,c_fill,g_auto/v1783423723/mindescapes/benefits/thsvjc0tupregnhoueg9.png",
    heroBotR:  "https://res.cloudinary.com/datmqdk2c/image/upload/f_auto,q_auto,w_900,h_560,c_fill,g_auto/v1783424175/mindescapes/benefits/zwg6s0toxzwg4vpeqp88.png",
    banner:    "https://res.cloudinary.com/datmqdk2c/image/upload/f_auto,q_auto,w_2400,h_1000,c_fill,g_auto/v1783426419/mindescapes/benefits/ta4rftra1b1j9wj2faic.png",
    cardTable: "https://res.cloudinary.com/datmqdk2c/image/upload/f_auto,q_auto,w_1600,c_limit,g_auto/v1783243482/mindescapes/benefits/alg8irqcuyudry3mtrk5.jpg",
    cardEscape:"https://res.cloudinary.com/datmqdk2c/image/upload/f_auto,q_auto,w_1600,c_limit,g_auto/v1783243496/mindescapes/benefits/gpyz58c5egnujvjyr7nr.jpg",
    cardEnjoy: "https://res.cloudinary.com/datmqdk2c/image/upload/f_auto,q_auto,w_1600,c_limit,g_auto/v1783243628/mindescapes/benefits/uktrykfuqnrvixb1jbc3.jpg",
    cardStay:  "https://res.cloudinary.com/datmqdk2c/image/upload/f_auto,q_auto,w_1600,c_limit,g_auto/v1783243471/mindescapes/benefits/at16d1mqytgfeu8dwaop.jpg",
};

/* Copy fallbacks · overridden by live tenant JSON where present. */
const COPY = {
    heroKicker:   "A Cadence Employee Benefit",
    heroTitle:    "Life, handled — so you can do your best work.",
    heroSubtitle: "From errands, appointments and documents to family care, travel and the gracefully last-minute — your LesConcierges® team is one message away. Anything. Anytime. Anywhere.",
    heroBody:     "One dedicated Concierge® for every request — so you spend less time on life admin and more on what matters, at work and at home.",
    heroCta:      "Employee sign in",
    bannerKicker: "The invisible workload, quietly handled",
    bannerTitle:  "One message. Everything arranged.",
    footerBlurb:  "A LesConcierges® employee benefit for Cadence. Anything. Anytime. Anywhere.",
    conciergeEyebrow: "Your LesConcierges® team",
    conciergeTitle:   "Whatever life needs,",
    conciergeAccent:  "just ask.",
    conciergeSub:     "Errands and appointments, family and care, travel and documents, celebrations and the last-minute — tell us, and we arrange it, quietly and completely.",
    phone:   "+91 87926 22424",
    phoneHref: "+918792622424",
    email:   "hello@lesconcierge.com",
    address: "#83, 3rd Floor, 7th Cross,\n4th B Block, Koramangala,\nBangalore-560034, India",
    whatsappPrefill: "Hi, I'm a Cadence employee and would like some help.",
};

/* Public tenant JSON endpoint — same origin, same slug across brands. */
const TENANT_API = "https://mindescapes.club/api/club/tenants/cadence";

/**
 * Append a <link> to <head> · returns the node so the effect can
 * remove it on unmount. Idempotent per href · never duplicates.
 */
function injectLink({ rel, href, crossOrigin }) {
    if (!href) return null;
    const existing = document.head.querySelector(`link[rel="${rel}"][href="${href}"]`);
    if (existing) return existing;
    const el = document.createElement("link");
    el.rel = rel;
    el.href = href;
    if (crossOrigin) el.crossOrigin = crossOrigin;
    document.head.appendChild(el);
    return el;
}

const T = {
    red:        "#E4002B",
    ink:        "#1A1A1A",
    inkSoft:    "#3B3736",
    inkFaint:   "#6C6763",
    bg:         "#F6F3EA",
    bgAlt:      "#F3F0E6",
    dark:       "#141618",
    goldDark:   "#c9a961",
    goldLight:  "#a98a4b",
    cardFill:   "#FBFAF4",
    line:       "rgba(26,26,26,0.10)",
    lineDark:   "rgba(255,255,255,0.10)",
    creamHi:    "rgba(246,243,234,0.92)",   /* 92% cream on dark */
    creamMid:   "rgba(246,243,234,0.82)",   /* 82% */
    creamLo:    "rgba(246,243,234,0.68)",   /* 68% */
    creamXlo:   "rgba(246,243,234,0.42)",   /* 42% (small print) */
};

/* Font imports · loaded once per page mount via a <style> block. */
const CADENCE_STYLES = `
/* Fonts are loaded via <link> injection in the page's useEffect —
   faster than an @import here (which is a two-hop request). */

.cadence-root {
    font-family: 'Archivo', system-ui, -apple-system, sans-serif;
    color: ${T.ink};
    background: ${T.bg};
    line-height: 1.55;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    min-height: 100vh;
    /* Warm grain overlay · very subtle */
    background-image:
        radial-gradient(rgba(180,140,90,0.045) 1px, transparent 1px);
    background-size: 3px 3px;
}
.cadence-root .cadence-serif {
    font-family: 'Fraunces', 'Palatino Linotype', 'Book Antiqua', Palatino, Georgia, serif;
    letter-spacing: -0.015em;
}
.cadence-root .cadence-italic { font-style: italic; }

.cadence-kicker {
    font-family: 'Archivo', sans-serif;
    font-size: 11px; letter-spacing: 0.24em;
    text-transform: uppercase; font-weight: 600;
    display: inline-flex; align-items: center; gap: 10px;
}
.cadence-kicker::before {
    content: ""; width: 6px; height: 6px; border-radius: 50%;
    background: ${T.goldLight}; display: inline-block;
}
.cadence-kicker.gold-dark::before { background: ${T.goldDark}; }
.cadence-kicker.lime::before { background: #C7E85B; box-shadow: 0 0 12px rgba(199,232,91,0.4); }

.cadence-pill {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 14px 30px; border-radius: 999px;
    font-family: 'Archivo', sans-serif;
    font-size: 14px; font-weight: 600; letter-spacing: 0.02em;
    text-decoration: none; cursor: pointer;
    transition: transform 220ms ease, box-shadow 220ms ease, background 220ms ease, color 220ms ease;
    border: 1px solid transparent;
    white-space: nowrap;
}
.cadence-pill--solid { background: ${T.red}; color: #fff; }
.cadence-pill--solid:hover { transform: translateY(-1px); box-shadow: 0 12px 28px -14px rgba(228,0,43,0.55); }
.cadence-pill--ghost { background: transparent; color: ${T.ink}; border-color: rgba(26,26,26,0.28); }
.cadence-pill--ghost:hover { background: ${T.ink}; color: ${T.bg}; border-color: ${T.ink}; }
.cadence-pill--ghost-dark { background: transparent; color: ${T.creamHi}; border-color: rgba(246,243,234,0.32); }
.cadence-pill--ghost-dark:hover { background: ${T.creamHi}; color: ${T.dark}; }
.cadence-pill--wa { background: #16A34A; color: #fff; box-shadow: 0 18px 40px -18px rgba(22,163,74,0.55); }
.cadence-pill--wa:hover { transform: translateY(-1px); background: #15803D; }

.cadence-h1 {
    font-family: 'Fraunces', Georgia, serif;
    font-weight: 500;
    font-size: clamp(38px, 5.6vw, 76px);
    line-height: 1.02; letter-spacing: -0.025em;
    color: ${T.ink}; margin: 0;
}
.cadence-h2 {
    font-family: 'Fraunces', Georgia, serif;
    font-weight: 500;
    font-size: clamp(32px, 4vw, 56px);
    line-height: 1.06; letter-spacing: -0.022em;
    margin: 0;
}

.cadence-card {
    background: ${T.cardFill};
    border-radius: 18px;
    box-shadow: 0 18px 44px -22px rgba(40,28,24,0.45);
    overflow: hidden;
    display: flex; flex-direction: column;
}

.cadence-header {
    position: sticky; top: 0; z-index: 40;
    background: ${T.bg}E6;
    backdrop-filter: blur(14px);
    -webkit-backdrop-filter: blur(14px);
    border-bottom: 1px solid ${T.line};
}

.cadence-footer-links a {
    color: ${T.creamMid}; text-decoration: none;
    transition: color 220ms ease;
}
.cadence-footer-links a:hover { color: #fff; }

@media (max-width: 900px) {
    .cadence-hero { grid-template-columns: 1fr !important; gap: 32px !important; }
    /* On mobile the text reads first · collage grows below. Prevents
       the mobile-shake caused by images loading and shoving text
       downward as they resolve. */
    .cadence-hero-text    { order: 1; }
    .cadence-hero-collage { order: 2; aspect-ratio: 4/3 !important; }
    .cadence-cards-grid { grid-template-columns: 1fr !important; }
    .cadence-footer-cols { grid-template-columns: 1fr !important; gap: 32px !important; }
}
`;

/* ─── Page component ─────────────────────────────────────────────── */
export default function Cadence() {
    // Live tenant data · falls back to hardcoded IMG + COPY on failure.
    // Design ruling 09 Jul 2026: page must never "loading spinner" — we
    // render the fallback instantly and swap in live values when they
    // arrive, so the first paint is always immediate and correct.
    const [tenant, setTenant] = React.useState(null);

    React.useEffect(() => {
        const prev = document.title;
        document.title = "Cadence × LesConcierges® — Life, handled.";

        // v3.49 · Discover Google Fonts as early as possible to shorten
        // the FOUT window · reduces the "shake" as Fraunces swaps in
        // for the Georgia fallback on the mobile hero headline.
        const preconnect1 = injectLink({ rel: "preconnect", href: "https://fonts.googleapis.com" });
        const preconnect2 = injectLink({ rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" });
        const fontsCss    = injectLink({
            rel: "stylesheet",
            href: "https://fonts.googleapis.com/css2?family=Archivo:wght@400;500;600;700&family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,500;0,9..144,600;0,9..144,700;1,9..144,400;1,9..144,500&display=swap",
        });

        const ctl = new AbortController();
        (async () => {
            try {
                const r = await fetch(TENANT_API, {
                    signal: ctl.signal,
                    // Public JSON · no cookies · same shape across all tenants.
                });
                if (!r.ok) return;
                const j = await r.json();
                setTenant(j);
            } catch { /* stay on fallback */ }
        })();
        return () => {
            document.title = prev;
            ctl.abort();
            [preconnect1, preconnect2, fontsCss].forEach((el) => el?.remove?.());
        };
    }, []);

    const resolved = React.useMemo(() => resolveTenant(tenant), [tenant]);

    return (
        <div className="cadence-root" data-testid="cadence-page">
            <style>{CADENCE_STYLES}</style>

            <Header logoChip={resolved.logoChip} />
            <Hero t={resolved} />
            <Banner t={resolved} />
            <ConciergeBand />
            <ConciergeCards t={resolved} />
            <Footer t={resolved} />
            <FloatingWhatsApp waHref={resolved.whatsappHref} />
        </div>
    );
}

/**
 * resolveTenant · merge live tenant JSON over hardcoded fallbacks.
 * Returns a flat object every section can consume without branching.
 */
function resolveTenant(tenant) {
    const landing = tenant?.landing || {};
    const pages   = landing.pages   || {};
    const pconc   = pages.concierge || {};
    const pi      = tenant?.pillar_images || {};
    const conc    = tenant?.concierge     || {};
    const waPhone = (conc.whatsapp || "").replace(/[^0-9]/g, "") || COPY.phoneHref;
    const waText  = conc.whatsapp_prefill || COPY.whatsappPrefill;

    // Two CTA labels come from the CMS · `cta_signin` (solid) and
    // `cta_apply` (ghost). If they match, we only render one — a
    // duplicate button reads as a mistake in the UI.
    const heroCta    = landing.cta_signin || COPY.heroCta;
    const heroCtaAlt = landing.cta_apply  || null;
    const showCtaAlt = !!heroCtaAlt && heroCtaAlt.trim() !== heroCta.trim();

    return {
        // Copy · prefer live values, fall back to constants.
        heroKicker:   landing.kicker      || COPY.heroKicker,
        heroTitle:    landing.title       || COPY.heroTitle,
        heroSubtitle: landing.subtitle    || COPY.heroSubtitle,
        heroBody:     landing.body        || COPY.heroBody,
        heroCta,
        heroCtaAlt,
        showCtaAlt,
        bannerKicker: landing.banner_kicker || COPY.bannerKicker,
        bannerTitle:  landing.banner_title  || COPY.bannerTitle,
        footerBlurb:  landing.footer_blurb  || COPY.footerBlurb,
        conciergeEyebrow: pconc.eyebrow      || COPY.conciergeEyebrow,
        conciergeTitle:   pconc.title        || COPY.conciergeTitle,
        conciergeAccent:  pconc.title_accent || COPY.conciergeAccent,
        conciergeSub:     pconc.subtitle     || COPY.conciergeSub,

        // Contact · registered_office and address are the same field.
        phone:   conc.phone   || COPY.phone,
        email:   conc.email   || COPY.email,
        address: conc.registered_office || conc.contact_address || COPY.address,

        // Images
        logoChip:  tenant?.logo_url    || IMG.logoChip,
        heroTall:  landing.hero_tall   || IMG.heroTall,
        heroTopR:  landing.hero_top    || IMG.heroTopR,
        heroBotR:  landing.hero_bot    || IMG.heroBotR,
        banner:    landing.banner      || IMG.banner,
        cards: {
            table:  pi.table  || IMG.cardTable,
            escape: pi.escape || IMG.cardEscape,
            enjoy:  pi.enjoy  || IMG.cardEnjoy,
            stay:   pi.stay   || IMG.cardStay,
        },

        // Deep links
        whatsappHref: `https://wa.me/${waPhone}?text=${encodeURIComponent(waText)}`,
    };
}

/* ─── §2 · Sticky header ─────────────────────────────────────────── */
function Header({ logoChip }) {
    return (
        <header className="cadence-header" data-testid="cadence-header">
            <div style={{
                maxWidth: 1320, margin: "0 auto",
                padding: "16px 28px",
                display: "flex", alignItems: "center", justifyContent: "space-between",
                gap: 16,
            }}>
                <a href="/cadence" style={{
                    display: "flex", alignItems: "center", gap: 12,
                    textDecoration: "none", color: T.ink,
                }} data-testid="cadence-header-logo">
                    <img src={logoChip} alt="Cadence"
                         style={{ width: 44, height: 44, borderRadius: 10, objectFit: "contain", background: "#fff", border: `1px solid ${T.line}`, padding: 4 }} />
                    <div style={{ lineHeight: 1.15 }}>
                        <div style={{
                            fontSize: 10, letterSpacing: "0.22em", textTransform: "uppercase",
                            color: T.inkFaint, fontWeight: 600,
                        }}>A Cadence Employee Benefit</div>
                        <div className="cadence-serif" style={{ fontSize: 20, fontWeight: 500, marginTop: 2 }}>
                            Cadence <span style={{ color: T.inkFaint, fontWeight: 400 }}>×</span> LesConcierges<sup style={{ fontSize: "0.55em" }}>®</sup>
                        </div>
                    </div>
                </a>

                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                    <a className="cadence-pill cadence-pill--ghost"
                       href={LINKS.signUp}
                       data-testid="cadence-header-signup"
                    >Sign up</a>
                    <a className="cadence-pill cadence-pill--solid"
                       href={LINKS.signIn}
                       data-testid="cadence-header-signin"
                    >Sign in</a>
                </div>
            </div>
        </header>
    );
}

/* ─── §3.1 · Hero (split layout + 3-image collage) ───────────────── */
function Hero({ t }) {
    return (
        <section className="cadence-hero" data-testid="cadence-hero"
            style={{
                maxWidth: 1320, margin: "0 auto",
                padding: "72px 28px 88px",
                display: "grid", gridTemplateColumns: "1.05fr 0.95fr",
                gap: 72, alignItems: "center",
            }}>
            {/* Left column */}
            <div className="cadence-hero-text">
                <div className="cadence-kicker gold-dark" style={{ color: T.goldLight }}
                     data-testid="cadence-hero-kicker">
                    {t.heroKicker}
                </div>
                <h1 className="cadence-h1" style={{ marginTop: 22 }}
                    data-testid="cadence-hero-title">
                    {t.heroTitle}
                </h1>
                <p className="cadence-serif cadence-italic" style={{
                    color: T.red, fontSize: "clamp(20px, 1.6vw, 24px)",
                    fontWeight: 500, marginTop: 26, marginBottom: 22, lineHeight: 1.4,
                }} data-testid="cadence-hero-subtitle"
                   dangerouslySetInnerHTML={{ __html: withRegSup(t.heroSubtitle) }} />
                <p style={{
                    color: T.inkFaint, fontSize: 16, lineHeight: 1.65,
                    maxWidth: 540, margin: 0,
                }} dangerouslySetInnerHTML={{ __html: withRegSup(t.heroBody) }} />
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 36 }}>
                    <a className="cadence-pill cadence-pill--solid" href={LINKS.signIn}
                       data-testid="cadence-hero-signin-solid">
                        {t.heroCta}
                    </a>
                    {t.showCtaAlt && (
                        <a className="cadence-pill cadence-pill--ghost" href={LINKS.signUp}
                           data-testid="cadence-hero-signup-ghost">
                            {t.heroCtaAlt}
                        </a>
                    )}
                </div>
            </div>

            {/* Right column · 3-image collage */}
            <div className="cadence-hero-collage" style={{
                display: "grid",
                gridTemplateColumns: "1.15fr 1fr",
                gridTemplateRows: "1fr 1fr",
                gap: 14,
                aspectRatio: "0.92/1",
            }}>
                <img src={t.heroTall} alt=""
                     loading="eager" decoding="async"
                     style={{
                         gridRow: "1 / span 2", gridColumn: "1",
                         width: "100%", height: "100%", objectFit: "cover",
                         borderRadius: 18,
                     }}
                     data-testid="cadence-hero-photo-tall" />
                <img src={t.heroTopR} alt=""
                     loading="eager" decoding="async"
                     style={{
                         gridRow: "1", gridColumn: "2",
                         width: "100%", height: "100%", objectFit: "cover",
                         borderRadius: 18,
                     }}
                     data-testid="cadence-hero-photo-top" />
                <img src={t.heroBotR} alt=""
                     loading="lazy" decoding="async"
                     style={{
                         gridRow: "2", gridColumn: "2",
                         width: "100%", height: "100%", objectFit: "cover",
                         borderRadius: 18,
                     }}
                     data-testid="cadence-hero-photo-bottom" />
            </div>
        </section>
    );
}

/**
 * Turn "LesConcierges®" / "Concierge®" (and their all-caps variant) into
 * `<sup>®</sup>` for consistent visual sizing when copy comes from
 * live CMS strings.
 */
function withRegSup(s) {
    if (!s) return "";
    // Escape any HTML the CMS might inject · then decorate ® only.
    const escaped = String(s)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    return escaped.replace(/®/g, '<sup style="font-size:0.55em">®</sup>');
}

/* ─── §3.2 · Full-width photo banner ─────────────────────────────── */
function Banner({ t }) {
    return (
        <section data-testid="cadence-banner" style={{
            position: "relative",
            height: "clamp(280px, 42vh, 460px)",
            overflow: "hidden",
        }}>
            <img src={t.banner} alt="" loading="lazy" decoding="async"
                 style={{
                     position: "absolute", inset: 0,
                     width: "100%", height: "100%", objectFit: "cover",
                 }} />
            <div style={{
                position: "absolute", inset: 0,
                background: "linear-gradient(180deg, rgba(20,22,24,0) 42%, rgba(20,22,24,0.68) 100%)",
            }} />
            <div style={{
                position: "absolute", left: 0, right: 0, bottom: 0,
                padding: "44px clamp(28px, 5vw, 72px)",
                color: "#fff",
                maxWidth: 1320, margin: "0 auto",
            }}>
                <div className="cadence-kicker lime" style={{ color: "#EAF7A8", marginBottom: 14 }}>
                    {t.bannerKicker}
                </div>
                <h2 className="cadence-h2" style={{ color: "#fff", maxWidth: 720 }}>
                    {splitBannerTitle(t.bannerTitle)}
                </h2>
            </div>
        </section>
    );
}

/**
 * "One message. Everything arranged." → ["One message.", "Everything arranged."]
 * The 2nd sentence renders in italic gold. If we can't confidently split,
 * we just render the whole title in gold on the second line for safety.
 */
function splitBannerTitle(title) {
    if (!title) return null;
    const idx = title.indexOf(". ");
    if (idx > 0 && idx < title.length - 2) {
        const first = title.slice(0, idx + 1);
        const rest  = title.slice(idx + 2);
        return (
            <>
                {first}{" "}
                <span className="cadence-italic" style={{ color: T.goldDark }}>{rest}</span>
            </>
        );
    }
    return <span className="cadence-italic" style={{ color: T.goldDark }}>{title}</span>;
}

/* ─── §3.3 · Dark Concierge® band ────────────────────────────────── */
function ConciergeBand() {
    const pillars = ["Table","Stay","Escape","Enjoy","Fit","Airport","Mobility","Exclusive","Care","Errands","Dreamweaver"];
    return (
        <section data-testid="cadence-concierge-band" style={{
            background: T.dark,
            padding: "clamp(72px, 10vw, 120px) 28px",
        }}>
            <div style={{ maxWidth: 980, margin: "0 auto", textAlign: "center" }}>
                <div className="cadence-kicker gold-dark" style={{ color: T.goldDark, justifyContent: "center" }}>
                    Your programme
                </div>
                <h2 className="cadence-h2" style={{
                    color: T.creamHi, marginTop: 22,
                    fontSize: "clamp(38px, 5vw, 68px)",
                }}>
                    A Concierge<sup style={{ fontSize: "0.5em" }}>®</sup>
                    &nbsp;<span className="cadence-italic" style={{ color: T.goldDark }}>of your own.</span>
                </h2>

                <p style={{
                    color: T.creamMid, fontSize: 17, lineHeight: 1.65,
                    maxWidth: 720, margin: "26px auto 0",
                }}>
                    One number, one team, one standard — a Concierge<sup style={{ fontSize: "0.55em" }}>®</sup> that
                    runs alongside every part of your life.
                </p>
                <p style={{
                    color: T.creamLo, fontSize: 15, lineHeight: 1.7,
                    maxWidth: 680, margin: "16px auto 0",
                }}>
                    Twelve pillars, from dining and travel to advisory and care — ask once, and
                    it is quietly arranged.
                </p>

                <div style={{
                    margin: "48px auto 0", maxWidth: 760,
                    borderTop: `1px solid ${T.lineDark}`,
                    paddingTop: 32,
                }}>
                    <div className="cadence-serif" style={{
                        color: T.creamMid, fontSize: 18, lineHeight: 1.7,
                        display: "flex", flexWrap: "wrap", justifyContent: "center",
                        gap: "6px 14px",
                    }} data-testid="cadence-pillar-row">
                        {pillars.map((p, i) => (
                            <React.Fragment key={p}>
                                <span>{p}</span>
                                {i < pillars.length - 1 && (
                                    <span style={{ color: T.goldDark, opacity: 0.7 }}>·</span>
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                </div>

                <p className="cadence-serif cadence-italic" style={{
                    color: T.goldDark, fontSize: "clamp(19px, 1.7vw, 24px)",
                    marginTop: 44, lineHeight: 1.5,
                }}>
                    Belonging anywhere in the world.<br />Where life flows without friction.
                </p>

                <a className="cadence-pill cadence-pill--solid"
                   href={LINKS.concierge}
                   data-testid="cadence-band-meet-concierge"
                   style={{ marginTop: 40 }}>
                    Meet your Concierge<sup style={{ fontSize: "0.55em" }}>®</sup> →
                </a>

                <p style={{
                    color: T.creamXlo, fontSize: 12, lineHeight: 1.7,
                    maxWidth: 700, margin: "56px auto 0",
                    textAlign: "center", letterSpacing: "0.01em",
                }}>
                    CONCIERGE<sup style={{ fontSize: "0.7em" }}>®</sup> is a registered word mark in
                    India, held across all 45 NICE classes and continuously used since 1998. What
                    you receive under this mark is not a promise. It is execution, with accountability.
                </p>
            </div>
        </section>
    );
}

/* ─── §3.4 · Concierge cards section ─────────────────────────────── */
const CARDS = [
    {
        key: "table",
        icon: "utensils",
        titleA: "Name the place.",
        titleB: "We hold the table.",
        body: "Global. Verified. Booking-first. Search any restaurant on earth.",
        img: IMG.cardTable,
    },
    {
        key: "escape",
        icon: "plane",
        titleA: "Where would you",
        titleB: "escape?",
        titleABeforeB: true,   // italic gold word is the closer here
        body: "Tours, activities and attractions — the Concierge® finds your right door.",
        img: IMG.cardEscape,
    },
    {
        key: "enjoy",
        icon: "ticket",
        titleA: "Name the night.",
        titleB: "We open the door.",
        body: "Live events, concerts, an unforgettable evening — wherever you are.",
        img: IMG.cardEnjoy,
    },
    {
        key: "stay",
        icon: "bed",
        titleA: "Anywhere on earth.",
        titleB: "We find the stay.",
        body: "Universal hotel search across 200+ countries — real photos, ratings and the right room.",
        img: IMG.cardStay,
    },
];

function ConciergeCards({ t }) {
    const cards = CARDS.map((c) => ({ ...c, img: t.cards[c.key] || c.img }));
    return (
        <section data-testid="cadence-cards-section" style={{
            background: T.bgAlt,
            padding: "clamp(80px, 10vw, 120px) 28px",
        }}>
            <div style={{ maxWidth: 1240, margin: "0 auto" }}>
                <div style={{ textAlign: "center", marginBottom: 56 }}>
                    <div className="cadence-kicker" style={{ color: T.goldLight, justifyContent: "center" }}
                         dangerouslySetInnerHTML={{ __html: withRegSup(t.conciergeEyebrow) }} />
                    <h2 className="cadence-h2" style={{
                        color: T.ink, marginTop: 20,
                        maxWidth: 900, marginLeft: "auto", marginRight: "auto",
                    }}>
                        {t.conciergeTitle}{" "}
                        <span className="cadence-italic" style={{ color: T.goldLight }}>
                            {t.conciergeAccent}
                        </span>
                    </h2>
                    <p style={{
                        color: T.inkFaint, fontSize: 16, lineHeight: 1.65,
                        maxWidth: 780, margin: "22px auto 0",
                    }}>
                        {t.conciergeSub}
                    </p>
                </div>

                <div className="cadence-cards-grid" style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4, 1fr)",
                    gap: 22,
                }}>
                    {cards.map((c) => <ConciergeCard key={c.key} card={c} />)}
                </div>

                <div style={{ textAlign: "center", marginTop: 56 }}>
                    <a className="cadence-pill cadence-pill--solid"
                       href={t.whatsappHref}
                       target="_blank" rel="noopener noreferrer"
                       data-testid="cadence-cards-talk-concierge">
                        Talk to your Concierge<sup style={{ fontSize: "0.55em" }}>®</sup> →
                    </a>
                </div>
            </div>
        </section>
    );
}

const ICONS = {
    utensils: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
            <path d="M3 2v7a2 2 0 0 0 2 2h1v11" /><path d="M7 2v9" /><path d="M11 2v9" /><path d="M15 2v20" /><path d="M19 2c1.5 0 2 3 2 5s-.5 5-2 5v10" />
        </svg>
    ),
    plane: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
            <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2Z" />
        </svg>
    ),
    ticket: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
            <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z" /><path d="M13 5v2" /><path d="M13 17v2" /><path d="M13 11v2" />
        </svg>
    ),
    bed: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
            <path d="M2 4v16" /><path d="M2 8h18a2 2 0 0 1 2 2v10" /><path d="M2 17h20" /><path d="M6 8v0" /><circle cx="7" cy="12" r="2" />
        </svg>
    ),
};

function ConciergeCard({ card }) {
    // Title layout: for /escape the italic-gold word closes the phrase.
    const titleNode = card.titleABeforeB ? (
        <>{card.titleA} <span className="cadence-italic" style={{ color: T.goldLight }}>{card.titleB}</span></>
    ) : (
        <>{card.titleA} <span className="cadence-italic" style={{ color: T.goldLight }}>{card.titleB}</span></>
    );
    return (
        <div className="cadence-card" data-testid={`cadence-card-${card.key}`}>
            <div style={{ position: "relative", height: 300, overflow: "hidden" }}>
                <img src={card.img} alt=""
                     loading="lazy" decoding="async"
                     style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                <div style={{
                    position: "absolute", left: 16, bottom: 16,
                    width: 44, height: 44, borderRadius: 12,
                    background: "#fff",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: T.ink,
                    boxShadow: "0 6px 18px -8px rgba(0,0,0,0.35)",
                }} aria-hidden="true">
                    {ICONS[card.icon]}
                </div>
            </div>
            <div style={{ padding: "26px 26px 30px", display: "flex", flexDirection: "column", flex: 1 }}>
                <h3 className="cadence-serif" style={{
                    fontSize: 22, fontWeight: 500, lineHeight: 1.2,
                    margin: 0, color: T.ink,
                }}>
                    {titleNode}
                </h3>
                <p style={{
                    color: T.inkFaint, fontSize: 14.5, lineHeight: 1.65,
                    margin: "14px 0 22px", flex: 1,
                }}>{card.body}</p>
                <a className="cadence-pill cadence-pill--solid"
                   href={LINKS.signIn}
                   data-testid={`cadence-card-cta-${card.key}`}
                   style={{ alignSelf: "flex-start", padding: "12px 24px", fontSize: 13 }}>
                    Sign in to request →
                </a>
            </div>
        </div>
    );
}

/* ─── §3.5 · Footer (dark ink) ───────────────────────────────────── */
function Footer({ t }) {
    // Phone href · normalize to E.164-ish tel: link.
    const phoneTel = "+" + (t.phone || "").replace(/[^0-9]/g, "");
    return (
        <footer data-testid="cadence-footer" style={{
            background: T.dark,
            color: T.creamMid,
            padding: "76px 28px 40px",
        }}>
            <div style={{ maxWidth: 1240, margin: "0 auto" }}>
                <div style={{ maxWidth: 620, marginBottom: 56 }}>
                    <div className="cadence-serif" style={{
                        color: T.creamHi, fontSize: 28, fontWeight: 500, letterSpacing: "-0.015em",
                    }}>
                        Cadence <span style={{ color: T.creamLo, fontWeight: 400 }}>×</span> LesConcierges<sup style={{ fontSize: "0.55em" }}>®</sup>
                    </div>
                    <p style={{
                        color: T.creamLo, fontSize: 15, lineHeight: 1.65, marginTop: 12,
                    }} dangerouslySetInnerHTML={{ __html: withRegSup(t.footerBlurb) }} />
                </div>

                <div className="cadence-footer-cols cadence-footer-links" style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: 64,
                    borderTop: `1px solid ${T.lineDark}`,
                    paddingTop: 44,
                }}>
                    <div>
                        <div style={{
                            fontSize: 11, letterSpacing: "0.24em", textTransform: "uppercase",
                            color: T.goldDark, fontWeight: 600, marginBottom: 14,
                        }}>Concierge</div>
                        <div style={{ fontSize: 14, lineHeight: 1.85 }}>
                            <a href={`tel:${phoneTel}`}>{t.phone}</a><br />
                            <a href={`mailto:${t.email}`}>{t.email}</a>
                        </div>
                    </div>
                    <div>
                        <div style={{
                            fontSize: 11, letterSpacing: "0.24em", textTransform: "uppercase",
                            color: T.goldDark, fontWeight: 600, marginBottom: 14,
                        }}>Contact us &amp; registered office</div>
                        <div style={{ fontSize: 14, lineHeight: 1.75, whiteSpace: "pre-line" }}>
                            {t.address}
                        </div>
                    </div>
                    <div>
                        <div style={{
                            fontSize: 11, letterSpacing: "0.24em", textTransform: "uppercase",
                            color: T.goldDark, fontWeight: 600, marginBottom: 14,
                        }}>Legal</div>
                        <div style={{ fontSize: 14, lineHeight: 1.85 }}>
                            <a href={LINKS.terms} data-testid="cadence-footer-terms">Terms &amp; Club Rules</a>
                        </div>
                    </div>
                </div>

                <div style={{
                    marginTop: 56, paddingTop: 24,
                    borderTop: `1px solid ${T.lineDark}`,
                    display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12,
                    fontSize: 12, color: T.creamLo,
                }}>
                    <div>© 2026 Cadence × LesConcierges<sup style={{ fontSize: "0.7em" }}>®</sup>.</div>
                    <div>Powered by Concierge<sup style={{ fontSize: "0.7em" }}>®</sup> and MindEscapes<sup style={{ fontSize: "0.7em" }}>®</sup></div>
                </div>
            </div>
        </footer>
    );
}

/* ─── Floating widget · WhatsApp pill (bottom-right) ─────────────── */
function FloatingWhatsApp({ waHref }) {
    return (
        <a className="cadence-pill cadence-pill--wa"
           href={waHref}
           target="_blank" rel="noopener noreferrer"
           data-testid="cadence-floating-whatsapp"
           style={{
               position: "fixed", right: 22, bottom: 22, zIndex: 50,
               padding: "13px 22px", fontSize: 14,
           }}>
            <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16" aria-hidden="true">
                <path d="M20.5 3.5A11 11 0 0 0 3 17l-1.4 5.1L7 20.8a11 11 0 0 0 5 1.2A11 11 0 0 0 20.5 3.5Zm-8.5 17a9 9 0 0 1-4.6-1.3l-.3-.2-3 .8.8-2.9-.2-.3A9 9 0 1 1 12 20.5Zm5-6.7c-.3-.2-1.6-.8-1.9-.9-.3-.1-.5-.2-.7.2-.2.3-.8.9-1 1.1-.2.2-.4.3-.7.1-.3-.2-1.3-.5-2.5-1.6-.9-.8-1.5-1.8-1.7-2.1-.2-.3 0-.5.1-.6.1-.1.3-.4.4-.5.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5 0-.2-.7-1.7-.9-2.3-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.4 0 1.4 1 2.8 1.2 3 .2.2 2.1 3.2 5.2 4.4.7.3 1.3.5 1.7.6.7.2 1.4.2 1.9.1.6-.1 1.7-.7 1.9-1.4.2-.7.2-1.3.2-1.4-.1-.1-.3-.2-.6-.3Z"/>
            </svg>
            Talk to a Concierge<sup style={{ fontSize: "0.6em" }}>®</sup>
        </a>
    );
}
